/**
 * The measurements true-up may adjust, and how far each is trusted: each
 * room's placement and size, the position and width of every door, window,
 * opening and alcove on a room's walls and its alcoves' walls, and each
 * alcove's depth. Each is read from a room and written back as a new room.
 */
import { EDGE_NAMES, type EdgeName, edgeSegments, type FloorPlanEdgeSegment, type FloorPlanRoom } from '@kaiizen/kai-floor-plan';

/** How far a room's placement is typically off (ft): it was dragged by hand. */
const PLACEMENT_SIGMA = 1 / 12;
/** How far a scanned dimension is typically off (ft). */
const MEASUREMENT_SIGMA = 0.5 / 12;

/** One adjustable measurement: how to read it from the rooms and write it back. */
export interface Parameter {
  /** The index of the room it belongs to. */
  room: number;
  sigma: number;
  /** A length, kept on the half-inch grid; placements are not. */
  isLength: boolean;
  /** What it measures, for the report: "moved", "width", "door width", "alcove depth". */
  what: string;
  get: (room: FloorPlanRoom) => number;
  set: (room: FloorPlanRoom, value: number) => FloorPlanRoom;
}

type SegmentField = 'position' | 'width' | 'depth';
const ALCOVE_WALLS = ['leftEdge', 'backEdge', 'rightEdge'] as const;

/** A segment on one of the room's walls, or on one of an alcove's own walls. */
interface SegmentPath {
  edge: EdgeName;
  index: number;
  alcoveWall?: { edge: (typeof ALCOVE_WALLS)[number]; index: number };
}

function segmentAt(room: FloorPlanRoom, { edge, index, alcoveWall }: SegmentPath): FloorPlanEdgeSegment | undefined {
  const segment = edgeSegments(room, edge)[index];
  return alcoveWall ? segment?.[alcoveWall.edge]?.segments[alcoveWall.index] : segment;
}

function setSegmentField(room: FloorPlanRoom, { edge, index, alcoveWall }: SegmentPath, field: SegmentField, value: number): FloorPlanRoom {
  const update = (segment: FloorPlanEdgeSegment): FloorPlanEdgeSegment => {
    if (!alcoveWall) return { ...segment, [field]: value };
    const wall = segment[alcoveWall.edge];
    if (!wall) return segment;
    return {
      ...segment,
      [alcoveWall.edge]: { ...wall, segments: wall.segments.map((item, itemIndex) => (itemIndex === alcoveWall.index ? { ...item, [field]: value } : item)) },
    };
  };
  return {
    ...room,
    [edge]: { ...room[edge], segments: edgeSegments(room, edge).map((segment, segmentIndex) => (segmentIndex === index ? update(segment) : segment)) },
  };
}

/**
 * Every measurement of room `index` that true-up may adjust: its x and y
 * placement first, then its width and length, then each wall segment's.
 */
export function roomParameters(room: FloorPlanRoom, index: number): Parameter[] {
  const parameters: Parameter[] = [
    { room: index, sigma: PLACEMENT_SIGMA, isLength: false, what: 'moved', get: current => current.x ?? 0, set: (current, x) => ({ ...current, x }) },
    { room: index, sigma: PLACEMENT_SIGMA, isLength: false, what: 'moved', get: current => current.y ?? 0, set: (current, y) => ({ ...current, y }) },
    { room: index, sigma: MEASUREMENT_SIGMA, isLength: true, what: 'width', get: current => current.width, set: (current, width) => ({ ...current, width }) },
    {
      room: index,
      sigma: MEASUREMENT_SIGMA,
      isLength: true,
      what: 'length',
      get: current => current.length,
      set: (current, length) => ({ ...current, length }),
    },
  ];
  const addSegmentParameters = (path: SegmentPath, fields: readonly SegmentField[]) => {
    const type = segmentAt(room, path)?.edgeSegmentType ?? 'segment';
    for (const field of fields) {
      parameters.push({
        room: index,
        sigma: MEASUREMENT_SIGMA,
        isLength: true,
        what: `${path.alcoveWall ? 'alcove ' : ''}${type} ${field}`,
        get: current => segmentAt(current, path)?.[field] ?? 0,
        set: (current, value) => setSegmentField(current, path, field, value),
      });
    }
  };
  for (const edge of EDGE_NAMES) {
    edgeSegments(room, edge).forEach((segment, segmentIndex) => {
      if (segment.edgeSegmentType !== 'alcove') return addSegmentParameters({ edge, index: segmentIndex }, ['position', 'width']);
      addSegmentParameters({ edge, index: segmentIndex }, ['position', 'width', 'depth']);
      for (const wall of ALCOVE_WALLS) {
        segment[wall]?.segments.forEach((_, wallIndex) =>
          addSegmentParameters({ edge, index: segmentIndex, alcoveWall: { edge: wall, index: wallIndex } }, ['position', 'width'])
        );
      }
    });
  }
  return parameters;
}
